<template>
  <div>
    <HeaderComponent/>

    <main class="container py-4">
      <h2 class="mb-1"><i class="mdi mdi-bookshelf text-primary me-2"></i>Library Services</h2>
      <p class="text-muted">Search the catalog, reserve a book, become a member, or book a space — pay directly via M-Pesa.</p>

      <!-- Tabs -->
      <ul class="nav nav-tabs my-3">
        <li class="nav-item"><a class="nav-link" :class="{ active: tab==='catalog' }"     @click="tab='catalog'"     href="javascript:;"><i class="mdi mdi-book-search me-1"></i>Catalog</a></li>
        <li class="nav-item"><a class="nav-link" :class="{ active: tab==='reserve' }"     @click="tab='reserve'"     href="javascript:;"><i class="mdi mdi-bookmark-plus me-1"></i>Reserve a Book</a></li>
        <li class="nav-item"><a class="nav-link" :class="{ active: tab==='membership' }"  @click="tab='membership'"  href="javascript:;"><i class="mdi mdi-account-plus me-1"></i>Become a Member</a></li>
        <li class="nav-item"><a class="nav-link" :class="{ active: tab==='space' }"       @click="tab='space'"       href="javascript:;"><i class="mdi mdi-room-service me-1"></i>Book a Space</a></li>
      </ul>

      <!-- ============ CATALOG SEARCH ============ -->
      <div v-if="tab === 'catalog'" class="card">
        <div class="card-body">
          <div class="row g-2 mb-3">
            <div class="col-md-7">
              <input v-model="catSearch" class="form-control" placeholder="Title, author, ISBN" @keyup.enter="loadCatalog(1)">
            </div>
            <div class="col-md-3">
              <input v-model="catCategory" class="form-control" placeholder="Category (optional)">
            </div>
            <div class="col-md-2"><button class="btn btn-primary w-100" @click="loadCatalog(1)" :disabled="catLoading">Search</button></div>
          </div>
          <div v-if="catLoading" class="text-center text-muted py-3">Loading…</div>
          <div v-else-if="!catalog.length" class="text-center text-muted py-3">No books matched.</div>
          <table v-else class="table table-sm align-middle">
            <thead class="table-light"><tr><th>Title</th><th>Author</th><th>ISBN</th><th>Category</th><th class="text-center">Available</th><th></th></tr></thead>
            <tbody>
              <tr v-for="b in catalog" :key="b.id">
                <td><strong>{{ b.title }}</strong></td>
                <td>{{ b.author || '—' }}</td>
                <td>{{ b.isbn || '—' }}</td>
                <td>{{ b.category || '—' }}</td>
                <td class="text-center">
                  <span class="badge" :class="b.availableCopies > 0 ? 'bg-success' : 'bg-warning text-dark'">
                    {{ b.availableCopies }} / {{ b.totalCopies }}
                  </span>
                </td>
                <td>
                  <button class="btn btn-sm btn-outline-primary" @click="goReserve(b)">Reserve</button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex justify-content-between" v-if="catCount > 0">
            <span class="text-muted small">{{ catCount }} title(s)</span>
            <div>
              <button class="btn btn-sm btn-outline-secondary me-1" :disabled="catPage <= 1" @click="loadCatalog(catPage-1)">Prev</button>
              <button class="btn btn-sm btn-outline-secondary" :disabled="catPage * 20 >= catCount" @click="loadCatalog(catPage+1)">Next</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ RESERVE ============ -->
      <div v-if="tab === 'reserve'" class="card">
        <div class="card-body">
          <div class="alert alert-info py-2 small">You must be an existing library member to reserve. If not, register first under <em>Become a Member</em>.</div>
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Membership identifier *</label>
              <div class="input-group">
                <input v-model="resMemberKw" class="form-control" placeholder="Membership No / phone / ID">
                <button class="btn btn-outline-secondary" @click="findResMember">Find</button>
              </div>
              <div v-if="resMember" class="small text-success mt-1">
                ✓ {{ resMember.membershipNo }} — {{ resMember.fullName }} ({{ resMember.status }})
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Pick book by title *</label>
              <div class="input-group">
                <input v-model="resTitleKw" class="form-control" placeholder="Title / author / ISBN">
                <button class="btn btn-outline-secondary" @click="findResBook">Find</button>
              </div>
              <div v-if="resBook" class="small text-success mt-1">
                ✓ {{ resBook.title }} — {{ resBook.author || 'unknown author' }}
              </div>
            </div>
            <div class="col-md-12">
              <label class="form-label">Pick up at branch *</label>
              <select v-model="resBranchID" class="form-select">
                <option value="">Choose…</option>
                <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
            <div class="col-md-12">
              <button class="btn btn-success" @click="doReserve" :disabled="resSubmitting || !resMember || !resBook || !resBranchID">
                Reserve Book
              </button>
            </div>
          </div>
          <div v-if="resResult" class="alert alert-info mt-3 mb-0">
            <h6 class="mb-2">Reservation created — position #{{ resResult.position }} in queue</h6>
            <div v-if="resResult.billNo">
              A reservation fee of <strong>KES {{ resResult.amount }}</strong> has been generated.<br>
              Bill: <code>{{ resResult.billNo }}</code>
              <button class="btn btn-sm btn-primary ms-2" @click="goPay(resResult.billNo)">Pay now via M-Pesa</button>
            </div>
            <div v-else class="text-muted">No fee required — you'll be notified when a copy is ready.</div>
          </div>
        </div>
      </div>

      <!-- ============ MEMBERSHIP ============ -->
      <div v-if="tab === 'membership'" class="card">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6"><label class="form-label">Full Name *</label><input v-model="regForm.fullName" class="form-control"></div>
            <div class="col-md-3"><label class="form-label">ID No.</label><input v-model="regForm.idNo" class="form-control"></div>
            <div class="col-md-3"><label class="form-label">Phone *</label><input v-model="regForm.phoneNumber" class="form-control"></div>
            <div class="col-md-6"><label class="form-label">Email</label><input v-model="regForm.email" type="email" class="form-control"></div>
            <div class="col-md-3">
              <label class="form-label">Category *</label>
              <select v-model="regForm.category" class="form-select">
                <option>ADULT</option><option>JUNIOR</option><option>INSTITUTION</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">Home Branch *</label>
              <select v-model="regForm.branchID" class="form-select">
                <option value="">Choose…</option>
                <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
            <div class="col-md-12">
              <button class="btn btn-primary" @click="register" :disabled="regSubmitting">
                Register
              </button>
            </div>
          </div>
          <div v-if="regResult" class="alert alert-info mt-3 mb-0">
            <h6 class="mb-2">Welcome — your membership number is <strong>{{ regResult.membershipNo }}</strong></h6>
            <div v-if="regResult.billNo">
              Registration fee: <strong>KES {{ regResult.amount }}</strong> &middot; Bill <code>{{ regResult.billNo }}</code>
              <button class="btn btn-sm btn-primary ms-2" @click="goPay(regResult.billNo)">Pay now via M-Pesa</button>
              <p class="small text-muted mt-2 mb-0">Your membership activates automatically once payment is confirmed.</p>
            </div>
            <div v-else>Your membership is active immediately (no fee configured).</div>
          </div>
        </div>
      </div>

      <!-- ============ BOOK A SPACE ============ -->
      <div v-if="tab === 'space'" class="card">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Branch *</label>
              <select v-model="spaceFilter.branchID" class="form-select" @change="loadSpaces">
                <option value="">All branches</option>
                <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
          </div>
          <div class="row mt-3" v-if="spaces.length">
            <div v-for="s in spaces" :key="s.id" class="col-md-6 col-lg-4 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <h5 class="card-title mb-1">{{ s.name }}</h5>
                  <p class="small text-muted mb-1">{{ s.branchName }} • capacity {{ s.capacity || '?' }}</p>
                  <p class="small mb-2">{{ s.description || '—' }}</p>
                  <p class="mb-2"><strong>KES {{ s.hourlyRate }}/hr</strong></p>
                  <button class="btn btn-sm btn-success w-100" @click="openSpaceBook(s)">Book this Space</button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-muted py-4">No spaces available.</div>

          <!-- Space booking inline form -->
          <div v-if="spaceForm.spaceID" class="alert alert-light border mt-3">
            <h6>Booking — {{ spaceFormName }}</h6>
            <div class="row g-2">
              <div class="col-md-4"><label class="form-label small">Name *</label><input v-model="spaceForm.customerName" class="form-control form-control-sm"></div>
              <div class="col-md-4"><label class="form-label small">Phone *</label><input v-model="spaceForm.customerPhone" class="form-control form-control-sm"></div>
              <div class="col-md-4"><label class="form-label small">Membership No (optional)</label>
                <div class="input-group input-group-sm">
                  <input v-model="spaceMemberKw" class="form-control" placeholder="LIB/2026/00042">
                  <button class="btn btn-outline-secondary" @click="linkSpaceMember">Link</button>
                </div>
              </div>
              <div class="col-md-6"><label class="form-label small">Start *</label><input v-model="spaceForm.startTime" type="datetime-local" class="form-control form-control-sm"></div>
              <div class="col-md-6"><label class="form-label small">End *</label><input v-model="spaceForm.endTime" type="datetime-local" class="form-control form-control-sm"></div>
            </div>
            <p v-if="spaceEstHours > 0" class="small mt-2 mb-2">
              Estimated: {{ spaceEstHours }} hr × KES {{ activeSpaceRate }} = <strong>KES {{ (spaceEstHours * activeSpaceRate).toLocaleString() }}</strong>
            </p>
            <div class="d-flex gap-2">
              <button class="btn btn-success btn-sm" @click="doBookSpace" :disabled="spaceSubmitting">Book + Generate Bill</button>
              <button class="btn btn-outline-secondary btn-sm" @click="spaceForm.spaceID = null">Cancel</button>
            </div>
            <div v-if="spaceResult" class="alert alert-info mt-3 mb-0">
              Booking #{{ spaceResult.bookingId }} (PENDING_PAYMENT) — {{ spaceResult.hours }} hr × KES {{ spaceResult.amount }}.<br>
              <span v-if="spaceResult.billNo">
                Bill <code>{{ spaceResult.billNo }}</code>
                <button class="btn btn-sm btn-primary ms-2" @click="goPay(spaceResult.billNo)">Pay now via M-Pesa</button>
              </span>
              <span v-else>(No fee configured)</span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <FooterComponent/>
  </div>
</template>

<script>
/* eslint-disable */
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { execute, library } from "@/api";

export default {
  name: "LibrarySelfService",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      tab: "catalog",
      branches: [],

      // Catalog
      catSearch: "", catCategory: "", catalog: [], catCount: 0, catPage: 1, catLoading: false,

      // Reserve
      resMemberKw: "", resMember: null,
      resTitleKw:  "", resBook:  null,
      resBranchID: "", resSubmitting: false, resResult: null,

      // Membership
      regForm: { fullName: "", idNo: "", phoneNumber: "", email: "", category: "ADULT", branchID: "" },
      regSubmitting: false, regResult: null,

      // Space
      spaces: [], spaceFilter: { branchID: "" },
      spaceForm: { spaceID: null, memberID: null, customerName: "", customerPhone: "", startTime: "", endTime: "" },
      spaceFormName: "", activeSpaceRate: 0,
      spaceMemberKw: "", spaceSubmitting: false, spaceResult: null,
    };
  },
  computed: {
    spaceEstHours() {
      if (!this.spaceForm.startTime || !this.spaceForm.endTime) return 0;
      const s = new Date(this.spaceForm.startTime).getTime();
      const e = new Date(this.spaceForm.endTime).getTime();
      if (!s || !e || e <= s) return 0;
      return Math.max(0.25, Math.round(((e - s) / 3600000) * 100) / 100);
    },
  },
  mounted() {
    this.loadBranches();
    this.loadCatalog(1);
    this.loadSpaces();
  },
  methods: {
    async loadBranches() {
      try {
        const fd = new FormData(); fd.append("function", "getBranches");
        const r = await execute(fd, library);
        if (r.data.success) this.branches = r.data.data.branches || [];
      } catch (e) { console.error(e); }
    },
    async loadCatalog(p) {
      this.catPage = p; this.catLoading = true;
      try {
        const fd = new FormData();
        fd.append("function", "getCatalogPaginate");
        fd.append("page", p);
        if (this.catSearch)   fd.append("search", this.catSearch);
        if (this.catCategory) fd.append("category", this.catCategory);
        const r = await execute(fd, library);
        if (r.data.success) { this.catalog = r.data.data.books || []; this.catCount = r.data.data.count || 0; }
        else { this.catalog = []; this.catCount = 0; }
      } catch (e) { console.error(e); }
      this.catLoading = false;
    },
    goReserve(b) {
      this.tab = "reserve";
      this.resBook = b;
      this.resTitleKw = b.title;
    },

    async findResMember() {
      if (!this.resMemberKw) return;
      const fd = new FormData(); fd.append("function", "searchMember"); fd.append("keyword", this.resMemberKw);
      const r = await execute(fd, library);
      if (r.data.success && r.data.data.member) this.resMember = r.data.data.member;
      else { this.resMember = null; alert(r.data.message || "Not found"); }
    },
    async findResBook() {
      if (!this.resTitleKw) return;
      const fd = new FormData();
      fd.append("function", "getCatalogPaginate"); fd.append("search", this.resTitleKw); fd.append("rows_per_page", "1");
      const r = await execute(fd, library);
      if (r.data.success && r.data.data.books && r.data.data.books.length) this.resBook = r.data.data.books[0];
      else { this.resBook = null; alert("No book matched"); }
    },
    async doReserve() {
      this.resSubmitting = true; this.resResult = null;
      try {
        const fd = new FormData();
        fd.append("function", "createReservation");
        fd.append("memberID", this.resMember.id);
        fd.append("catalogID", this.resBook.id);
        fd.append("branchID", this.resBranchID);
        const r = await execute(fd, library);
        if (r.data.success) this.resResult = r.data.data;
        else alert(r.data.message);
      } catch (e) { alert(e.message); }
      this.resSubmitting = false;
    },

    async register() {
      if (!this.regForm.fullName || !this.regForm.phoneNumber || !this.regForm.branchID) {
        alert("Full name, phone and branch are required"); return;
      }
      this.regSubmitting = true; this.regResult = null;
      try {
        const fd = new FormData();
        fd.append("function", "registerMember");
        for (const k in this.regForm) fd.append(k, this.regForm[k]);
        const r = await execute(fd, library);
        if (r.data.success) this.regResult = r.data.data;
        else alert(r.data.message);
      } catch (e) { alert(e.message); }
      this.regSubmitting = false;
    },

    async loadSpaces() {
      try {
        const fd = new FormData(); fd.append("function", "getSpaces");
        if (this.spaceFilter.branchID) fd.append("branchID", this.spaceFilter.branchID);
        const r = await execute(fd, library);
        if (r.data.success) this.spaces = r.data.data.spaces || []; else this.spaces = [];
      } catch (e) { console.error(e); }
    },
    openSpaceBook(s) {
      this.spaceForm = { spaceID: s.id, memberID: null, customerName: "", customerPhone: "", startTime: "", endTime: "" };
      this.spaceFormName = s.name; this.activeSpaceRate = Number(s.hourlyRate) || 0;
      this.spaceMemberKw = ""; this.spaceResult = null;
    },
    async linkSpaceMember() {
      if (!this.spaceMemberKw) return;
      const fd = new FormData(); fd.append("function", "searchMember"); fd.append("keyword", this.spaceMemberKw);
      const r = await execute(fd, library);
      if (r.data.success && r.data.data.member) {
        const m = r.data.data.member;
        this.spaceForm.memberID = m.id;
        this.spaceForm.customerName = m.fullName;
        this.spaceForm.customerPhone = m.phoneNumber;
      } else alert(r.data.message || "Member not found");
    },
    async doBookSpace() {
      if (!this.spaceForm.customerName || !this.spaceForm.customerPhone || !this.spaceForm.startTime || !this.spaceForm.endTime) {
        alert("Name, phone, start and end are required"); return;
      }
      if (this.spaceEstHours <= 0) { alert("End must be after start"); return; }
      this.spaceSubmitting = true; this.spaceResult = null;
      try {
        const fd = new FormData();
        fd.append("function", "bookSpace");
        for (const k in this.spaceForm) if (this.spaceForm[k] != null && this.spaceForm[k] !== "") fd.append(k, this.spaceForm[k]);
        const r = await execute(fd, library);
        if (r.data.success) this.spaceResult = r.data.data;
        else alert(r.data.message);
      } catch (e) { alert(e.message); }
      this.spaceSubmitting = false;
    },

    goPay(billNo) {
      this.$router.push({ path: `/payment/${billNo}` });
    },
  },
};
</script>
